import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { createChartOfAccount, listChartOfAccounts, updateChartOfAccount } from '../../api/finance';
import { usePermissions } from '../../context/PermissionContext';
import {
  Button,
  ClickableRow,
  Drawer,
  DrawerField,
  EmptyState,
  ErrorNote,
  FieldLabel,
  ListToolbar,
  LoadingNote,
  PageHeader,
  Select,
  StatusBadge,
  SummaryStrip,
  TextInput,
  tableStyle,
  tdStyle,
  thStyle,
  usePagination,
  Pagination,
} from '../../components/common';
import type { ChartOfAccount } from '../../types/domain';

const ACCOUNT_TYPE_LABEL: Record<ChartOfAccount['account_type'], string> = {
  ASSET: 'Aset',
  LIABILITY: 'Liabilitas',
  EQUITY: 'Ekuitas',
  REVENUE: 'Pendapatan',
  EXPENSE: 'Beban',
};

/**
 * Chart of Accounts (FASE 10, migration 0044). Tenant-wide (no branch_id
 * column — accounts are not branch-scoped in this schema). No accounting
 * taxonomy is invented here — account_type/normal_balance are exactly the
 * canonical CHECK-constrained values (ASSET/LIABILITY/EQUITY/REVENUE/
 * EXPENSE, DEBIT/CREDIT).
 */
export default function ChartOfAccountsPage() {
  const { hasPermission } = usePermissions();
  const [accounts, setAccounts] = useState<ChartOfAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [selected, setSelected] = useState<ChartOfAccount | null>(null);

  const canManage = hasPermission('chart_of_account', 'create');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setAccounts(await listChartOfAccounts());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return accounts.filter((a) => {
      if (q && !a.name.toLowerCase().includes(q) && !a.account_code.toLowerCase().includes(q)) return false;
      if (typeFilter !== 'ALL' && a.account_type !== typeFilter) return false;
      return true;
    });
  }, [accounts, search, typeFilter]);
  const { pageRows, page, pageCount, setPage, start, pageSize, total } = usePagination(filtered, 20);

  const byType = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of accounts) m.set(a.account_type, (m.get(a.account_type) ?? 0) + 1);
    return m;
  }, [accounts]);

  return (
    <div>
      <PageHeader title="Chart of Accounts" subtitle="Daftar akun akuntansi — dasar untuk semua posting jurnal. Migrasi 0044." />
      {canManage && (
        <Button variant="primary" onClick={() => setShowForm((v) => !v)} style={{ marginBottom: 16 }}>
          {showForm ? 'Batal' : '+ Akun baru'}
        </Button>
      )}
      {showForm && canManage && (
        <NewAccountForm
          accounts={accounts}
          onCreated={() => {
            setShowForm(false);
            void load();
          }}
        />
      )}
      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}
      {!loading && !error && (
        <>
          <SummaryStrip
            items={[
              { label: 'Total Akun', value: accounts.length },
              { label: 'Aset', value: byType.get('ASSET') ?? 0 },
              { label: 'Liabilitas', value: byType.get('LIABILITY') ?? 0 },
              { label: 'Pendapatan', value: byType.get('REVENUE') ?? 0 },
              { label: 'Beban', value: byType.get('EXPENSE') ?? 0 },
            ]}
          />
          <ListToolbar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Cari kode/nama akun..."
            resultCount={total}
            filters={
              <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={{ width: 160 }}>
                <option value="ALL">Semua tipe</option>
                {Object.entries(ACCOUNT_TYPE_LABEL).map(([k, label]) => (
                  <option key={k} value={k}>
                    {label}
                  </option>
                ))}
              </Select>
            }
          />
          {accounts.length === 0 && <EmptyState text="Belum ada akun. Buat akun pertama untuk mulai memposting jurnal." />}
          {accounts.length > 0 && total === 0 && <EmptyState text="Tidak ada akun yang cocok dengan pencarian/filter." />}
          {total > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Kode</th>
                  <th style={thStyle}>Nama</th>
                  <th style={thStyle}>Tipe</th>
                  <th style={thStyle}>Saldo Normal</th>
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((a) => (
                  <ClickableRow key={a.account_id} onClick={() => setSelected(a)}>
                    <td style={{ ...tdStyle, color: 'var(--alex-primary)', fontWeight: 600 }}>{a.account_code}</td>
                    <td style={tdStyle}>{a.name}</td>
                    <td style={tdStyle}>{ACCOUNT_TYPE_LABEL[a.account_type]}</td>
                    <td style={tdStyle}>{a.normal_balance === 'DEBIT' ? 'Debit' : 'Kredit'}</td>
                    <td style={tdStyle}>
                      <StatusBadge status={a.status} />
                    </td>
                  </ClickableRow>
                ))}
              </tbody>
            </table>
          )}
          <Pagination page={page} pageCount={pageCount} setPage={setPage} start={start} pageSize={pageSize} total={total} />
        </>
      )}
      <AccountDetailDrawer
        account={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        accounts={accounts}
        canEdit={canManage}
        onChanged={() => void load()}
      />
    </div>
  );
}

function NewAccountForm({ accounts, onCreated }: { accounts: ChartOfAccount[]; onCreated: () => void }) {
  const [accountCode, setAccountCode] = useState('');
  const [name, setName] = useState('');
  const [accountType, setAccountType] = useState<ChartOfAccount['account_type']>('ASSET');
  const [normalBalance, setNormalBalance] = useState<ChartOfAccount['normal_balance']>('DEBIT');
  const [parentAccountId, setParentAccountId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await createChartOfAccount(accountCode.trim(), name.trim(), accountType, normalBalance, parentAccountId || null, true);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ marginBottom: 20, padding: 16, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div style={{ width: 120 }}>
        <FieldLabel label="Kode Akun">
          <TextInput value={accountCode} onChange={(e) => setAccountCode(e.target.value)} required autoFocus placeholder="1000" />
        </FieldLabel>
      </div>
      <div style={{ flex: 1, minWidth: 180 }}>
        <FieldLabel label="Nama Akun">
          <TextInput value={name} onChange={(e) => setName(e.target.value)} required placeholder="Kas / Bank" />
        </FieldLabel>
      </div>
      <div style={{ width: 150 }}>
        <FieldLabel label="Tipe">
          <Select value={accountType} onChange={(e) => setAccountType(e.target.value as ChartOfAccount['account_type'])}>
            {Object.entries(ACCOUNT_TYPE_LABEL).map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </div>
      <div style={{ width: 130 }}>
        <FieldLabel label="Saldo Normal">
          <Select value={normalBalance} onChange={(e) => setNormalBalance(e.target.value as ChartOfAccount['normal_balance'])}>
            <option value="DEBIT">Debit</option>
            <option value="CREDIT">Kredit</option>
          </Select>
        </FieldLabel>
      </div>
      <div style={{ width: 180 }}>
        <FieldLabel label="Induk Akun (opsional)">
          <Select value={parentAccountId} onChange={(e) => setParentAccountId(e.target.value)}>
            <option value="">—</option>
            {accounts.map((a) => (
              <option key={a.account_id} value={a.account_id}>
                {a.account_code} — {a.name}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </div>
      <Button type="submit" variant="primary" loading={submitting}>
        Simpan
      </Button>
      {error && <ErrorNote text={error} />}
    </form>
  );
}

function AccountDetailDrawer({
  account,
  open,
  onClose,
  accounts,
  canEdit,
  onChanged,
}: {
  account: ChartOfAccount | null;
  open: boolean;
  onClose: () => void;
  accounts: ChartOfAccount[];
  canEdit: boolean;
  onChanged: () => void;
}) {
  const [name, setName] = useState('');
  const [parentAccountId, setParentAccountId] = useState('');
  const [isPostable, setIsPostable] = useState(true);
  const [status, setStatus] = useState<ChartOfAccount['status']>('ACTIVE');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!account) return;
    setName(account.name);
    setParentAccountId(account.parent_account_id ?? '');
    setIsPostable(account.is_postable);
    setStatus(account.status);
    setError(null);
  }, [account?.account_id]);

  if (!account) return null;

  const handleSave = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await updateChartOfAccount(account.account_id, name.trim(), parentAccountId || null, isPostable, status);
      onChanged();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer open={open} onClose={onClose} title={account.name}>
      <DrawerField label="Kode Akun" value={account.account_code} />
      <DrawerField label="Tipe" value={ACCOUNT_TYPE_LABEL[account.account_type]} />
      <DrawerField label="Saldo Normal" value={account.normal_balance === 'DEBIT' ? 'Debit' : 'Kredit'} />
      {canEdit ? (
        <>
          <div style={{ marginTop: 12 }}>
            <FieldLabel label="Nama Akun">
              <TextInput value={name} onChange={(e) => setName(e.target.value)} />
            </FieldLabel>
          </div>
          <div style={{ marginTop: 10 }}>
            <FieldLabel label="Induk Akun">
              <Select value={parentAccountId} onChange={(e) => setParentAccountId(e.target.value)}>
                <option value="">—</option>
                {accounts
                  .filter((a) => a.account_id !== account.account_id)
                  .map((a) => (
                    <option key={a.account_id} value={a.account_id}>
                      {a.account_code} — {a.name}
                    </option>
                  ))}
              </Select>
            </FieldLabel>
          </div>
          <div style={{ marginTop: 10, display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <FieldLabel label="Dapat Diposting">
                <Select value={isPostable ? 'YES' : 'NO'} onChange={(e) => setIsPostable(e.target.value === 'YES')}>
                  <option value="YES">Ya</option>
                  <option value="NO">Tidak (akun header/grup)</option>
                </Select>
              </FieldLabel>
            </div>
            <div style={{ flex: 1 }}>
              <FieldLabel label="Status">
                <Select value={status} onChange={(e) => setStatus(e.target.value as ChartOfAccount['status'])}>
                  <option value="ACTIVE">Aktif</option>
                  <option value="INACTIVE">Nonaktif</option>
                </Select>
              </FieldLabel>
            </div>
          </div>
          <Button variant="primary" onClick={() => void handleSave()} loading={submitting} style={{ marginTop: 12 }}>
            Simpan Perubahan
          </Button>
          {error && <ErrorNote text={error} />}
        </>
      ) : (
        <>
          <DrawerField label="Dapat Diposting" value={account.is_postable ? 'Ya' : 'Tidak (akun header/grup)'} />
          <DrawerField label="Status" value={<StatusBadge status={account.status} />} />
        </>
      )}
    </Drawer>
  );
}
